"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { type Media as MediaType } from "@/payload-types";
import { cn } from "@/lib/utils";

const INTERSECTION_OBSERVER_OPTIONS: IntersectionObserverInit = {
	rootMargin: "5% 0px 5% 0px",
};

const VIDEO_ASPECT_RATIO = "56.25%";

/**
 * Converts YouTube or Vimeo URL to embed URL with optional autoplay.
 *
 * @param url - The YouTube or Vimeo URL to convert.
 * @param autoplay - Whether to autoplay the video (will be muted).
 * @returns The embed URL or null if URL is invalid.
 */
function getEmbedUrl(url: string | null | undefined, autoplay: boolean = false): string | null {
	if (!url) return null;

	const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
	const youtubeMatch = url.match(youtubeRegex);
	if (youtubeMatch) {
		const videoId = youtubeMatch[1];
		const params = new URLSearchParams();
		if (autoplay) {
			params.set("autoplay", "1");
			params.set("mute", "1");
		}
		const queryString = params.toString();
		return `https://www.youtube.com/embed/${videoId}${queryString ? `?${queryString}` : ""}`;
	}

	const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
	const vimeoMatch = url.match(vimeoRegex);
	if (vimeoMatch) {
		const videoId = vimeoMatch[1];
		const params = new URLSearchParams();
		if (autoplay) {
			params.set("autoplay", "1");
			params.set("muted", "1");
		}
		const queryString = params.toString();
		return `https://player.vimeo.com/video/${videoId}${queryString ? `?${queryString}` : ""}`;
	}

	return null;
}

/**
 * Custom hook for intersection observer-based video autoplay.
 *
 * @param elementRef - Ref to the element to observe.
 * @param enabled - Whether the observer should be active.
 * @param onIntersect - Callback when element intersects viewport.
 * @param onLeave - Optional callback when element leaves viewport.
 */
function useVideoIntersectionObserver(
	elementRef: React.RefObject<HTMLElement | null>,
	enabled: boolean,
	onIntersect: () => void,
	onLeave?: () => void
) {
	useEffect(() => {
		const element = elementRef.current;
		if (!enabled || !element) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						onIntersect();
					} else if (onLeave) {
						onLeave();
					}
				});
			},
			INTERSECTION_OBSERVER_OPTIONS
		);

		observer.observe(element);

		return () => {
			observer.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, onIntersect, onLeave]);
}

interface VideoFileProps {
	src: string;
	autoplay: boolean;
	wrapperClassName?: string;
}

/**
 * Video file component with intersection observer-based autoplay and lazy loading.
 */
function VideoFile({ src, autoplay, wrapperClassName }: VideoFileProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [shouldLoad, setShouldLoad] = useState(autoplay);

	const handleIntersect = useCallback(() => {
		if (!shouldLoad) {
			setShouldLoad(true);
		} else if (autoplay && videoRef.current) {
			videoRef.current.play().catch(() => {
			});
		}
	}, [autoplay, shouldLoad]);

	const handleLeave = useCallback(() => {
		if (autoplay && videoRef.current) {
			videoRef.current.pause();
		}
	}, [autoplay]);

	useVideoIntersectionObserver(videoRef, !shouldLoad || autoplay, handleIntersect, handleLeave);

	const videoElement = (
		<video
			ref={videoRef}
			src={shouldLoad ? src : undefined}
			controls
			muted={autoplay}
			loop={autoplay}
			playsInline
			preload="none"
			className="w-full h-auto"
		>
			Your browser does not support the video tag.
		</video>
	);

	if (wrapperClassName) {
		return <div className={cn(wrapperClassName)}>{videoElement}</div>;
	}

	return videoElement;
}

interface VideoEmbedProps {
	url: string;
	autoplay: boolean;
	wrapperClassName?: string;
}

/**
 * Video embed component with intersection observer-based autoplay and lazy loading.
 */
function VideoEmbed({ url, autoplay, wrapperClassName }: VideoEmbedProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [embedUrl, setEmbedUrl] = useState<string | null>(null);

	const handleIntersect = useCallback(() => {
		if (!embedUrl) {
			setEmbedUrl(getEmbedUrl(url, autoplay));
		}
	}, [url, embedUrl, autoplay]);

	useVideoIntersectionObserver(containerRef, !embedUrl, handleIntersect);

	const wrapperClasses = cn("relative w-full", wrapperClassName);
	const aspectRatioStyle = { paddingBottom: VIDEO_ASPECT_RATIO };

	if (!embedUrl) {
		return (
			<div ref={containerRef} className={wrapperClasses} style={aspectRatioStyle}>
				<div className="absolute inset-0 flex items-center justify-center">
					<span>Loading video...</span>
				</div>
			</div>
		);
	}

	return (
		<div ref={containerRef} className={wrapperClasses} style={aspectRatioStyle}>
			<iframe
				src={embedUrl}
				className="absolute top-0 left-0 w-full h-full"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
				loading="lazy"
				title="Embedded video"
			/>
		</div>
	);
}

interface VideoProps {
	sourceType: "file" | "embed";
	/**
	 * Video file from Payload CMS (for sourceType="file").
	 */
	file?: (number | null) | MediaType;
	/**
	 * External video URL (for sourceType="embed").
	 */
	embedUrl?: string | null;
	autoplay?: boolean;
	wrapperClassName?: string;
}

/**
 * Renders video content (file upload or embed) with optional autoplay on scroll.
 *
 * @param props - Component props.
 * @returns The rendered video element or null if no valid video is provided.
 */
export const Video: React.FC<VideoProps> = ({
	sourceType,
	file,
	embedUrl,
	autoplay = false,
	wrapperClassName,
}) => {
	if (sourceType === "file") {
		if (!file || typeof file === "number" || !file.url) {
			return null;
		}

		return <VideoFile src={file.url} autoplay={autoplay} wrapperClassName={wrapperClassName} />;
	}

	if (sourceType === "embed") {
		if (!embedUrl) {
			return null;
		}

		return <VideoEmbed url={embedUrl} autoplay={autoplay} wrapperClassName={wrapperClassName} />;
	}

	return null;
};
